import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';

interface MarkdownRendererProps {
  content: string;
  className?: string;
  colorScheme?: 'gray' | 'orange' | 'yellow';
  baseUrl?: string; // Base URL for converting relative links to absolute
}

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ 
  content, 
  className = '', 
  colorScheme = 'gray',
  baseUrl
}) => {
  const getColorClasses = (colorScheme: string) => {
    const colors: Record<string, { link: string; blockquote: string }> = {
      gray: {
        link: 'text-blue-600 hover:text-blue-800',
        blockquote: 'border-yellow-400'
      },
      orange: {
        link: 'text-orange-600 hover:text-orange-800',
        blockquote: 'border-orange-400'
      },
      yellow: {
        link: 'text-blue-600 hover:text-blue-800',
        blockquote: 'border-yellow-400'
      }
    };
    return colors[colorScheme] || colors.gray;
  };

  const colors = getColorClasses(colorScheme);

  // Helper function to convert relative URLs to absolute URLs
  const getAbsoluteUrl = (href: string | undefined) => {
    if (!href) return '#';
    
    // If already absolute URL, return as is
    if (href.startsWith('http://') || href.startsWith('https://') || href.startsWith('mailto:') || href.startsWith('tel:')) {
      return href;
    }
    
    // If we have a baseUrl and href is relative, combine them
    if (baseUrl && href.startsWith('/')) {
      // Remove trailing slash from baseUrl if it exists
      const cleanBaseUrl = baseUrl.replace(/\/$/, '');
      return `https://${cleanBaseUrl}${href}`;
    }
    
    // If baseUrl and relative path without leading slash
    if (baseUrl && !href.startsWith('/')) {
      const cleanBaseUrl = baseUrl.replace(/\/$/, '');
      return `https://${cleanBaseUrl}/${href}`;
    }
    
    // Return as is if no baseUrl provided
    return href;
  };

  return (
    <div className={`prose prose-${colorScheme} max-w-none prose-img:rounded-lg prose-img:shadow-md prose-headings:font-semibold prose-a:text-blue-600 hover:prose-a:text-blue-800 ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        disallowedElements={[]}
        skipHtml={false}
        components={{
          h1: ({ children }) => (
            <h1 className="text-2xl font-bold mb-6 mt-8 text-gray-900 block">
              {children}
            </h1>
          ),
          h2: ({ children }) => (
            <h2 className="text-xl font-semibold mb-5 mt-8 text-gray-900 block">
              {children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3 className="text-lg font-semibold mb-4 mt-6 text-gray-900 block">
              {children}
            </h3>
          ),
          h4: ({ children }) => (
            <h4 className="text-base font-semibold mb-3 mt-5 text-gray-900 block">
              {children}
            </h4>
          ),
          h5: ({ children }) => (
            <h5 className="text-sm font-semibold mb-3 mt-4 text-gray-900 block">
              {children}
            </h5>
          ),
          h6: ({ children }) => (
            <h6 className="text-xs font-semibold mb-2 mt-3 text-gray-900 block">
              {children}
            </h6>
          ),
          p: ({ children }) => (
            <p className="mb-5 mt-2 leading-7 text-gray-700 block">
              {children}
            </p>
          ),
          a: ({ href, children }) => (
            <a 
              href={getAbsoluteUrl(href)} 
              target="_blank" 
              rel="noopener noreferrer" 
              className={`${colors.link} underline`}
            >
              {children}
            </a>
          ),
          strong: ({ children }) => (
            <strong className="font-semibold text-gray-900">
              {children}
            </strong>
          ),
          em: ({ children }) => (
            <em className="italic text-gray-800">
              {children}
            </em>
          ),
          ul: ({ children }) => (
            <ul className="list-disc list-inside mb-6 mt-3 space-y-2 block">
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol className="list-decimal list-inside mb-6 mt-3 space-y-2 block">
              {children}
            </ol>
          ),
          li: ({ children }) => (
            <li className="ml-4 mb-2 text-gray-700 leading-6 block">
              {children}
            </li>
          ),
          img: ({ src, alt }) => {
            let imageUrl = src || '';

            // Convert relative URLs to absolute first
            if (imageUrl && !imageUrl.startsWith('http')) {
              imageUrl = getAbsoluteUrl(imageUrl);
            }

            return (
              <img
                src={imageUrl}
                alt={alt || 'Image'}
                className="max-w-full h-auto rounded-lg my-4 mx-auto shadow-sm hover:shadow-md transition-shadow duration-200"
                loading="lazy"
                onError={(e) => {
                  // Hide broken images gracefully
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  console.warn('Failed to load image:', imageUrl);
                }}
              />
            );
          },
          blockquote: ({ children }) => (
            <blockquote className={`border-l-4 ${colors.blockquote} pl-6 pr-4 py-3 italic text-gray-600 my-6 bg-gray-50 rounded-r-lg block leading-7`}>
              {children}
            </blockquote>
          ),
          code: ({ children }) => (
            <code className="bg-gray-100 px-2 py-1 rounded font-mono text-sm text-gray-800">
              {children}
            </code>
          ),
          pre: ({ children }) => (
            <pre className="bg-gray-100 p-4 rounded-lg overflow-x-auto my-6 block">
              {children}
            </pre>
          ),
          table: ({ children }) => (
            <div className="my-6 overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 border border-gray-200">
                {children}
              </table>
            </div>
          ),
          thead: ({ children }) => (
            <thead className="bg-gray-50">
              {children}
            </thead>
          ),
          tbody: ({ children }) => (
            <tbody className="bg-white divide-y divide-gray-200">
              {children}
            </tbody>
          ),
          tr: ({ children }) => (
            <tr className="hover:bg-gray-50">
              {children}
            </tr>
          ),
          th: ({ children }) => (
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className="px-4 py-3 text-sm text-gray-700 whitespace-pre-wrap">
              {children}
            </td>
          ),
          hr: () => (
            <hr className="my-8 border-t-2 border-gray-300 block" />
          )
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownRenderer;